// Best-effort server-side mirror of recorded expenses/photos, written by the PHP
// endpoints under public/api/ (shipped verbatim into dist/api/ by Vite's publicDir
// copy, so they only run when served through a real PHP-capable host — not under
// `npm run dev`, which just serves the .php file as static text). IndexedDB
// (lib/db.ts) remains the app's only real source of truth; this is a one-way,
// append-only export to docroot/data — nothing is ever read back from it.
//
// Every mutation here follows the same shape: try to POST immediately; if that
// fails for any reason (offline, server unreachable, wrong host), queue it in
// IndexedDB and retry automatically once the app is back online (state.ts calls
// flushSyncQueue() on the 'online' event and once at boot), so a trip recorded
// entirely offline still ends up mirrored once connectivity returns.

import { queueSync, getAllSyncQueue, deleteSyncItem, bumpSyncAttempts } from './db';
import { uid } from './format';
import type { ExpenseMirrorPayload, PhotoMirrorPayload } from '../types';

async function postExpense(payload: ExpenseMirrorPayload): Promise<boolean> {
  try {
    const res = await fetch('/api/log-expense.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    // A non-PHP host (e.g. the Vite dev server) still answers 200 with the raw
    // .php source as text — only trust an actual {ok:true} JSON body as success.
    const json = await res.json().catch(() => null);
    return res.ok && json?.ok === true;
  } catch {
    return false;
  }
}

async function postPhoto(payload: PhotoMirrorPayload, blob: Blob): Promise<boolean> {
  try {
    const fd = new FormData();
    fd.append('id', payload.id);
    fd.append('capturedAt', payload.capturedAt);
    fd.append('lat', payload.lat == null ? '' : String(payload.lat));
    fd.append('lng', payload.lng == null ? '' : String(payload.lng));
    fd.append('place', payload.place ?? '');
    fd.append('createdAt', payload.createdAt);
    fd.append('original', blob, `${payload.id}.jpg`);
    const res = await fetch('/api/log-photo.php', { method: 'POST', body: fd });
    const json = await res.json().catch(() => null);
    return res.ok && json?.ok === true;
  } catch {
    return false;
  }
}

export function mirrorExpense(payload: ExpenseMirrorPayload): void {
  postExpense(payload).then((ok) => {
    if (!ok) {
      queueSync({ id: uid(), createdAt: new Date().toISOString(), attempts: 0, item: { kind: 'expense', payload } });
    }
  });
}

export function mirrorPhoto(payload: PhotoMirrorPayload, blob: Blob): void {
  postPhoto(payload, blob).then((ok) => {
    if (!ok) {
      queueSync({
        id: uid(),
        createdAt: new Date().toISOString(),
        attempts: 0,
        item: { kind: 'photo', payload, blob },
      });
    }
  });
}

/** Resends queued mirror POSTs in the order they were created, stopping at the
 * first failure — preserves CSV row ordering and avoids hammering a server that's
 * still unreachable. Safe to call opportunistically (boot, 'online' event). */
export async function flushSyncQueue(): Promise<void> {
  if (!navigator.onLine) return;
  const items = await getAllSyncQueue();
  for (const q of items) {
    const ok = q.item.kind === 'expense' ? await postExpense(q.item.payload) : await postPhoto(q.item.payload, q.item.blob);
    if (ok) {
      await deleteSyncItem(q.id);
    } else {
      await bumpSyncAttempts(q.id);
      break;
    }
  }
}
